import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, AlertTriangle, Shield, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import heroImage from "@/assets/blog-deportation-defense.jpg";
import { generateArticleSchema } from "@/lib/seo-schemas";

const DeportationDefense = () => {
  const schema = generateArticleSchema(
    "What to Do If You're Facing Deportation",
    "Critical steps to take immediately if you receive a Notice to Appear or are facing removal proceedings.",
    "2025-01-10",
    "2025-01-10",
    "https://danielalbertlaw.com/blog/what-to-do-facing-deportation"
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="What to Do If You're Facing Deportation | Daniel Albert Law"
        description="Critical steps to take immediately if you receive a Notice to Appear or are facing removal proceedings. Experienced deportation defense attorney guidance."
        keywords="deportation defense, removal proceedings, Notice to Appear, immigration attorney Houston, deportation lawyer"
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: "What to Do If You're Facing Deportation" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                Immigration
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>January 10, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
              What to Do If You're Facing Deportation
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Emergency Alert */}
            <Card className="mb-12 border-destructive bg-destructive/10">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-8 w-8 text-destructive flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold mb-3 text-destructive">This is an Emergency Situation</h2>
                    <p className="text-lg mb-4">
                      If you have received a Notice to Appear (NTA) or are facing deportation proceedings, time is critical. Contact an immigration attorney immediately at <a href="tel:8329303059" className="font-bold underline">(832) 930-3059</a>.
                    </p>
                    <Button asChild size="lg" variant="destructive">
                      <a href="tel:8329303059">
                        <Phone className="mr-2 h-5 w-5" />
                        Call Now for Emergency Help
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Facing deportation (also called "removal") is one of the most stressful situations an immigrant can experience. However, receiving a Notice to Appear doesn't mean deportation is inevitable. There are legal defenses and options available, but taking immediate action is crucial to protect your rights and your future in the United States.
              </p>
            </div>

            {/* Main Content Sections */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-bold mb-6">Immediate Steps to Take</h2>
                <div className="space-y-6">
                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">1</span>
                      Do Not Ignore the Notice
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Never ignore a Notice to Appear (NTA) or any immigration court documents. Failing to appear in court will result in an automatic deportation order in your absence (called an "in absentia" order), which is much harder to fight later.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">2</span>
                      Contact an Immigration Attorney Immediately
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      The single most important step is to hire an experienced immigration attorney as soon as possible. An attorney can review your case, identify potential defenses, and represent you in court. Statistics show that represented individuals are significantly more likely to avoid deportation.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">3</span>
                      Gather All Immigration Documents
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Collect all immigration-related documents including passports, visas, entry stamps, work permits, prior immigration applications, and any correspondence with USCIS or ICE. These documents are crucial for building your defense.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">4</span>
                      Document Your Ties to the United States
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Start gathering evidence of your ties to the U.S., including employment records, tax returns, lease agreements, utility bills, children's school records, medical records, and letters from community members. This evidence can be crucial for certain defenses.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">5</span>
                      Avoid Travel Outside the U.S.
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Do not leave the United States if you are in removal proceedings. Leaving the country can be considered abandonment of your case and may result in an automatic deportation order.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Common Defenses Against Deportation</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Depending on your circumstances, several legal defenses may be available:
                </p>
                <ul className="space-y-4 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Cancellation of Removal:</strong> Available to certain individuals who have been in the U.S. for at least 10 years and whose removal would cause exceptional hardship to a U.S. citizen or permanent resident spouse, parent, or child.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Asylum:</strong> If you fear persecution in your home country based on race, religion, nationality, political opinion, or membership in a particular social group.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Adjustment of Status:</strong> If you're eligible for a green card through family relationship, employment, or other means.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Withholding of Removal:</strong> Similar to asylum but with a higher burden of proof, preventing deportation to a specific country where you would face persecution.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Prosecutorial Discretion:</strong> In some cases, ICE may exercise discretion to close or administratively close your case.
                    </div>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Understanding the Removal Process</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  The deportation process typically involves several stages:
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">Master Calendar Hearing</h3>
                    <p className="text-muted-foreground">
                      Your first court appearance where you'll receive information about your case and enter a plea to the charges against you.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">Individual Hearing (Merits Hearing)</h3>
                    <p className="text-muted-foreground">
                      A full hearing where evidence is presented, witnesses testify, and the judge makes a final decision on your case.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">Appeals</h3>
                    <p className="text-muted-foreground">
                      If the judge orders deportation, you may have the right to appeal to the Board of Immigration Appeals (BIA) and potentially to federal court.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">What NOT to Do</h2>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't sign any documents without consulting an attorney first</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't lie or provide false information to immigration officials</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't use a "notario" or unlicensed immigration consultant</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't accept voluntary departure without understanding the consequences</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't wait until the last minute to seek legal help</span>
                  </li>
                </ul>
              </section>
            </div>

            {/* Related Services */}
            <Card className="mt-12 bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Facing Deportation? We Can Help</h2>
                <p className="text-lg mb-6 opacity-95">
                  Daniel Albert Law Firm has successfully defended countless clients facing deportation. Our experienced team provides aggressive, compassionate representation in all removal proceedings. Contact us for a consultation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/practice-areas/immigration-services">
                      Learn More About Deportation Defense
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <CTASection
        title="Don't Wait - Get Legal Help Now"
        description="Every day matters in deportation cases. Contact us immediately for an emergency consultation."
      />

      <Footer />
    </div>
  );
};

export default DeportationDefense;